import { useState, useCallback, useEffect } from "react";
import logger from "../utils/logger.js";

/**
 * Global error handler hook for managing application-wide errors
 * Provides centralized error handling, logging, and user notification
 */
export const useGlobalErrorHandler = () => {
  const [errors, setErrors] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Handle network status changes
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Handle unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      logger.error("Unhandled promise rejection:", {
        reason: event.reason,
        promise: event.promise,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });

      addError({
        type: "UNHANDLED_REJECTION",
        message: "An unexpected error occurred. Please try again.",
        details: event.reason?.toString() || "Unknown error",
        timestamp: new Date().toISOString(),
      });
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  /**
   * Add an error to the global error state
   */
  const addError = useCallback((error) => {
    const errorWithId = {
      id: Date.now().toString(36) + Math.random().toString(36).substr(2),
      timestamp: new Date().toISOString(),
      ...error,
    };

    setErrors((prev) => [...prev, errorWithId]);

    // Auto-remove error after 10 seconds for non-critical errors
    if (!error.persistent) {
      setTimeout(() => {
        removeError(errorWithId.id);
      }, 10000);
    }

    return errorWithId.id;
  }, []);

  /**
   * Remove an error from the global error state
   */
  const removeError = useCallback((errorId) => {
    setErrors((prev) => prev.filter((error) => error.id !== errorId));
  }, []);

  /**
   * Clear all errors
   */
  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  /**
   * Handle Firebase errors with specific error messages
   */
  const handleFirebaseError = useCallback(
    (error, context = "") => {
      let userMessage = "An error occurred. Please try again.";
      let errorType = "FIREBASE_ERROR";

      // Map Firebase error codes to user-friendly messages
      switch (error.code) {
        case "auth/user-not-found":
        case "auth/wrong-password":
          userMessage = "Invalid email or password.";
          errorType = "AUTH_ERROR";
          break;
        case "auth/too-many-requests":
          userMessage = "Too many failed attempts. Please try again later.";
          errorType = "RATE_LIMIT_ERROR";
          break;
        case "auth/network-request-failed":
          userMessage = "Network error. Please check your connection.";
          errorType = "NETWORK_ERROR";
          break;
        case "permission-denied":
          userMessage = "You do not have permission to perform this action.";
          errorType = "PERMISSION_ERROR";
          break;
        case "unavailable":
          userMessage = "Service temporarily unavailable. Please try again.";
          errorType = "SERVICE_UNAVAILABLE";
          break;
        case "quota-exceeded":
          userMessage = "Service quota exceeded. Please contact support.";
          errorType = "QUOTA_ERROR";
          break;
        default:
          if (error.message) {
            userMessage = error.message;
          }
      }

      logger.error(`Firebase error in ${context}:`, {
        code: error.code,
        message: error.message,
        stack: error.stack,
        context,
        timestamp: new Date().toISOString(),
      });

      return addError({
        type: errorType,
        message: userMessage,
        details: error.message,
        context,
        code: error.code,
      });
    },
    [addError],
  );

  /**
   * Handle network errors
   */
  const handleNetworkError = useCallback(
    (error, context = "") => {
      const isOfflineError = !navigator.onLine;
      const userMessage = isOfflineError
        ? "You appear to be offline. Please check your connection."
        : "Network error. Please check your connection and try again.";

      logger.error(`Network error in ${context}:`, {
        message: error.message,
        stack: error.stack,
        context,
        isOnline: navigator.onLine,
        timestamp: new Date().toISOString(),
      });

      return addError({
        type: "NETWORK_ERROR",
        message: userMessage,
        details: error.message,
        context,
        isOffline: isOfflineError,
      });
    },
    [addError],
  );

  /**
   * Handle validation errors
   */
  const handleValidationError = useCallback(
    (errors, context = "") => {
      const errorMessages = Array.isArray(errors) ? errors : [errors];
      const userMessage =
        errorMessages.length === 1
          ? errorMessages[0]
          : "Please correct the highlighted fields.";

      logger.warn(`Validation error in ${context}:`, {
        errors: errorMessages,
        context,
        timestamp: new Date().toISOString(),
      });

      return addError({
        type: "VALIDATION_ERROR",
        message: userMessage,
        details: errorMessages,
        context,
      });
    },
    [addError],
  );

  /**
   * Handle generic application errors
   */
  const handleError = useCallback(
    (error, context = "", options = {}) => {
      const {
        userMessage = "An unexpected error occurred. Please try again.",
        persistent = false,
        type = "APPLICATION_ERROR",
      } = options;

      logger.error(`Application error in ${context}:`, {
        message: error.message,
        stack: error.stack,
        context,
        timestamp: new Date().toISOString(),
      });

      return addError({
        type,
        message: userMessage,
        details: error.message,
        context,
        persistent,
      });
    },
    [addError],
  );

  /**
   * Retry a failed operation with exponential backoff
   */
  const retryOperation = useCallback(
    async (operation, maxRetries = 3, baseDelay = 1000) => {
      let lastError;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          return await operation();
        } catch (error) {
          lastError = error;

          if (attempt === maxRetries) {
            throw error;
          }

          // Exponential backoff with jitter
          const delay =
            baseDelay * Math.pow(2, attempt - 1) + Math.random() * 1000;
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }

      throw lastError;
    },
    [],
  );

  return {
    errors,
    isOnline,
    addError,
    removeError,
    clearErrors,
    handleFirebaseError,
    handleNetworkError,
    handleValidationError,
    handleError,
    retryOperation,
  };
};
